import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { theme } from 'antd';

export type ThemeMode = 'light' | 'dark';

/** Плотность UI: "comfortable" — дефолт AntD (componentSize=middle, ~32px высота
 * кнопок, padding'и просторные); "compact" — для power-users (componentSize=small,
 * ~24px, плотнее списки, больше строк на экран). Tabular/list-страницы выигрывают
 * больше всех. Меняется через AppHeader toggle, перcист в localStorage. */
export type DensityMode = 'comfortable' | 'compact';

interface ThemeState {
  mode: ThemeMode;
  density: DensityMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  setDensity: (density: DensityMode) => void;
  toggleDensity: () => void;
}

// Получаем тему по умолчанию из localStorage или системных настроек
const getDefaultTheme = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return 'dark';
  }
  
  // Проверяем сохранённую тему через persist storage
  try {
    const saved = localStorage.getItem('theme-storage');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.state?.mode === 'light' || parsed.state?.mode === 'dark') {
        return parsed.state.mode;
      }
    }
  } catch (e) {
    // Игнорируем ошибки парсинга
  }
  
  // По умолчанию темная тема
  return 'dark';
};

const getDefaultDensity = (): DensityMode => {
  if (typeof window === 'undefined') return 'comfortable';
  try {
    const saved = localStorage.getItem('theme-storage');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.state?.density === 'compact' || parsed.state?.density === 'comfortable') {
        return parsed.state.density;
      }
    }
  } catch (e) {
    // ignore
  }
  return 'comfortable';
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: getDefaultTheme(),
      density: getDefaultDensity(),
      setMode: (mode: ThemeMode) => {
        set({ mode });
        // Применяем тему к документу
        if (typeof document !== 'undefined') {
          document.documentElement.setAttribute('data-theme', mode);
        }
      },
      toggleMode: () => {
        const currentMode = get().mode;
        const newMode = currentMode === 'light' ? 'dark' : 'light';
        set({ mode: newMode });
        // Применяем тему к документу
        if (typeof document !== 'undefined') {
          document.documentElement.setAttribute('data-theme', newMode);
        }
      },
      setDensity: (density: DensityMode) => {
        set({ density });
        if (typeof document !== 'undefined') {
          document.documentElement.setAttribute('data-density', density);
        }
      },
      toggleDensity: () => {
        const cur = get().density;
        const next = cur === 'comfortable' ? 'compact' : 'comfortable';
        set({ density: next });
        if (typeof document !== 'undefined') {
          document.documentElement.setAttribute('data-density', next);
        }
      },
    }),
    {
      name: 'theme-storage',
      storage: createJSONStorage(() => localStorage),
      // Сохраняем mode + density (остальные методы пропускаются)
      partialize: (state) => ({ mode: state.mode, density: state.density }),
      // Применяем тему + density при инициализации
      onRehydrateStorage: () => (state) => {
        if (state && typeof document !== 'undefined') {
          document.documentElement.setAttribute('data-theme', state.mode);
          document.documentElement.setAttribute('data-density', state.density);
        }
      },
    }
  )
);

// Получаем конфигурацию темы Ant Design
export const getAntdTheme = (mode: ThemeMode) => {
  const shared = {
    colorPrimary:         '#6366f1',   // brand-500
    colorPrimaryHover:    '#818cf8',   // brand-400
    colorPrimaryActive:   '#4f46e5',   // brand-600
    borderRadius:          6,
    borderRadiusLG:        8,
    borderRadiusSM:        4,
    fontFamily:           "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontFamilyCode:       "'JetBrains Mono', ui-monospace, Menlo, Monaco, 'Courier New', monospace",
  };

  if (mode === 'dark') {
    return {
      algorithm: theme.darkAlgorithm,
      token: {
        ...shared,
        colorBgBase:        '#070809',
        colorBgContainer:   '#0f1115',
        colorBgElevated:    '#171a21',
        colorBgLayout:      '#070809',
        colorBorder:        '#22262f',
        colorBorderSecondary: '#2e3340',
        colorText:          '#e6e8ec',
        colorTextSecondary: '#a0a6b2',
        colorTextTertiary:  '#6b7280',
        colorTextDisabled:  '#4b5563',
        colorLink:          '#818cf8',
        colorLinkHover:     '#6366f1',
      },
    };
  }

  return {
    algorithm: theme.defaultAlgorithm,
    token: {
      ...shared,
      colorBgBase:        '#ffffff',
      colorBgContainer:   '#ffffff',
      colorBgElevated:    '#ffffff',
      colorBgLayout:      '#f7f8fa',
      colorBorder:        '#e5e7eb',
      colorBorderSecondary: '#d1d5db',
      colorText:          '#0f1115',
      colorTextSecondary: '#4b5563',
      colorTextTertiary:  '#6b7280',
      colorLink:          '#6366f1',
      colorLinkHover:     '#4f46e5',
    },
  };
};
